import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose } from "react-icons/io5";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaShip,
  FaMoneyBillWave,
  FaUser,
  FaInfoCircle,
  FaUsers,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { yachts, coupons, yachtBookings } from "../../services/api";
import moment from "moment";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import logo from "../../assets/images/logo.png";

const CreatePartnerYachtBookingModal = ({
  isOpen,
  onClose,
  partner,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [availableYachts, setAvailableYachts] = useState([]);
  const [selectedYacht, setSelectedYacht] = useState(null);
  const [selectedYachtDetails, setSelectedYachtDetails] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [cruiseOptions, setCruiseOptions] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [couponStatus, setCouponStatus] = useState({
    isValid: false,
    discount: 0,
    message: "",
  });
  const [destinations, setDestinations] = useState({
                                    delos: false,
                                    rhenia: false,
                                    mykonos: false
                                  });
  const [errors, setErrors] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [availableDestinations, setAvailableDestinations] = useState([]);
  const [priceBreakdown, setPriceBreakdown] = useState(null);

  const [formData, setFormData] = useState({
    bookingDate: null,
    bookingTime: "",
    cruiseType: "",
    destinations: [],
    numberOfGuests: 1,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    couponCode: "",
    notes: "",
  });

  useEffect(() => {
    if (isOpen) {
      fetchYachts();
    } else {
      // Reset the form when closing
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      bookingDate: null,
      bookingTime: "",
      cruiseType: "",
      destinations: [],
      numberOfGuests: 1,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      couponCode: "",
      notes: "",
    });
    setSelectedYacht(null);
    setSelectedYachtDetails(null);
    setCouponStatus({ isValid: false, discount: 0, message: "" });
    setErrors({});
    setTotalPrice(0);
    setDatePickerOpen(false);
    setCruiseOptions([]);
    setTimeSlots([]);
    setAvailableDestinations([]);
    setPriceBreakdown(null);
  };

  const fetchYachts = async () => {
    try {
      setInitialLoading(true);
      const response = await yachts.getAll();
      setAvailableYachts(
        response.data.filter((yacht) => yacht.status === "available")
      );
      setInitialLoading(false);
    } catch (error) {
      console.error("Error fetching yachts:", error);
      setInitialLoading(false);
    }
  };

  const fetchYachtDetails = async (yachtId) => {
    try {
      setLoading(true);
      const response = await yachts.getById(yachtId);
      setSelectedYachtDetails(response.data);

      // Extract cruise options from yacht data
      const options = response.data.cruiseOptions.map((option) => ({
        value: option.type,
        label: option.type,
        durationHours: option.durationHours,
        timeSlots: option.timeSlots,
      }));

      setCruiseOptions(options);

      // Set available destinations
      setAvailableDestinations(response.data.destinations?.available || []);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching yacht details:", error);
      setLoading(false);
    }
  };

  const handleYachtSelect = (yacht) => {
    setSelectedYacht(yacht);
    fetchYachtDetails(yacht._id);

    // Reset cruise-specific form fields when changing yachts
    setFormData((prev) => ({
      ...prev,
      cruiseType: "",
      bookingTime: "",
      destinations: [],
    }));
    setTimeSlots([]);
  };

  const handleCruiseTypeChange = (e) => {
    const selectedCruiseType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      cruiseType: selectedCruiseType,
      bookingTime: "", // Reset time when cruise type changes
    }));

    // Find selected cruise option to get available time slots
    const selectedOption = cruiseOptions.find(
      (option) => option.value === selectedCruiseType
    );
    setTimeSlots(selectedOption?.timeSlots || []);
  };

  // Replace the API-based price calculation with manual calculation
  const calculatePrice = useCallback(() => {
    if (
      !selectedYachtDetails ||
      !formData.bookingDate ||
      !formData.cruiseType
    ) {
      return 0;
    }
    return calculatePriceManually();
  }, [
    selectedYachtDetails,
    formData.bookingDate,
    formData.cruiseType,
    formData.numberOfGuests,
    couponStatus,
  ]);

  // Manual price calculation to handle guest tiers
  const calculatePriceManually = () => {
    console.log("Using manual price calculation");

    if (
      !selectedYachtDetails?.pricing ||
      !formData.bookingDate ||
      !formData.cruiseType
    ) {
      return 0;
    }

    // Determine cruise type category and duration type
    let cruiseCategory = ""; // "private" or "semiPrivate"
    let durationType = ""; // "halfDay" or "fullDay"
    

    if (formData.cruiseType.toLowerCase().includes("semi")) {
      cruiseCategory = "semiPrivate";
    } else if (formData.cruiseType.toLowerCase().includes("private")) {
      cruiseCategory = "private";
    } else {
      console.warn("Unknown cruise type:", formData.cruiseType);
      return 0;
    }

    if (formData.cruiseType.toLowerCase().includes("half")) {
      durationType = "halfDay";
    } else if (formData.cruiseType.toLowerCase().includes("full")) {
      durationType = "fullDay";
    } else {
      console.warn("Unknown duration type:", formData.cruiseType);
      return 0;
    }

    // Determine season based on selected date
    const month = formData.bookingDate.getMonth(); // 0-based (0 = January, 11 = December)
    let seasonType = "";

    // April(3), May(4), September(8), October(9)
    if ([3, 4, 8, 9].includes(month)) {
      seasonType = "lowSeason";
    }
    // June(5), July(6), August(7)
    else if ([5, 6, 7].includes(month)) {
      seasonType = "highSeason";
    }
    // All other months now use mediumSeason if available
    else {
      seasonType = "mediumSeason";
    }

    console.log(
      `Pricing lookup: ${cruiseCategory}.${durationType}.${seasonType}`
    );

    // Get pricing info from nested structure
    const seasonPricing =
      selectedYachtDetails.pricing?.[cruiseCategory]?.[durationType]?.[
        seasonType
      ];

    if (!seasonPricing) {
      console.warn(
        `No pricing found for ${cruiseCategory}.${durationType}.${seasonType}`
      );
      setErrors((prev) => ({
        ...prev,
        availability: `No pricing available for ${formData.cruiseType} during this season.`,
      }));
      return 0;
    }

    console.log("Season pricing:", seasonPricing);

    // Handle guest tier pricing
    if (seasonPricing.guestTiers && seasonPricing.guestTiers.length > 0) {
      console.log("Using guest tier pricing");

      // Find the appropriate tier for the number of guests
      const numGuests = formData.numberOfGuests || 1;
      let matchingTier = null;

      for (const tier of seasonPricing.guestTiers) {
        const guestRange = tier.guests.split("-").map((num) => parseInt(num));

        if (guestRange.length === 2) {
          const [minGuests, maxGuests] = guestRange;
          if (numGuests >= minGuests && numGuests <= maxGuests) {
            matchingTier = tier;
            break;
          }
        } else if (guestRange.length === 1) {
          if (numGuests === guestRange[0]) {
            matchingTier = tier;
            break;
          }
        }
      }

      if (matchingTier) {
        console.log(
          `Found matching tier: ${matchingTier.guests} guests at rate ${matchingTier.rate}`
        );
        // Apply discount
        const discount = couponStatus.isValid ? couponStatus.discount : 0;
        const finalPrice = Math.max(0, matchingTier.rate - discount);
        console.log(`Final price after discount: ${finalPrice}`);
        return finalPrice;
      } else {
        // If no exact match, try to find the highest tier
        if (seasonPricing.guestTiers.length > 0) {
          const highestTier =
            seasonPricing.guestTiers[seasonPricing.guestTiers.length - 1];
          console.log(
            `No exact tier match. Using highest tier: ${highestTier.guests} at rate ${highestTier.rate}`
          );

          // Apply discount
          const discount = couponStatus.isValid ? couponStatus.discount : 0;
          const finalPrice = Math.max(0, highestTier.rate - discount);
          return finalPrice;
        }
      }
    }


    // Fall back to fixed rate or per guest rate if no tiers or no matching tier
    if (seasonPricing.fixedRate) {
      console.log(`Using fixed rate: ${seasonPricing.fixedRate}`);
      const discount = couponStatus.isValid ? couponStatus.discount : 0;
      return Math.max(0, seasonPricing.fixedRate - discount);
    } else if (seasonPricing.perGuestRate && formData.numberOfGuests > 0) {
      // Semi-private per guest calculation
      const totalPrice = seasonPricing.perGuestRate * formData.numberOfGuests;
      console.log(
        `Using per guest rate: ${seasonPricing.perGuestRate} × ${formData.numberOfGuests} = ${totalPrice}`
      );

      // Apply discount
      const discount = couponStatus.isValid ? couponStatus.discount : 0;
      const finalPrice = Math.max(0, totalPrice - discount);
      return finalPrice;
    }

    console.warn("No applicable pricing found");
    setErrors((prev) => ({
      ...prev,
      availability: "Unable to determine pricing for the selected options.",
    }));
    return 0;
  };

  
                                  const handleDestinationChange = (e) => {
                                    const { name, checked } = e.target;
                                    
                                    // For half-day cruises, only allow one selection
                                    if (formData.cruiseType.toLowerCase().includes('half') && checked) {
                                      // Reset all to false first
                                      const newDestinations = {
                                        delos: false,
                                        rhenia: false,
                                        mykonos: false
                                      };
                                      // Then set the selected one to true
                                      newDestinations[name] = true;
                                      setDestinations(newDestinations);
                                    } else {
                                      // For full day or deselection
                                      setDestinations(prev => ({
                                        ...prev,
                                        [name]: checked
                                      }));
                                    }
                                  };

                                  const handleDurationChange = (e) => {
                                    const duration = e.target.value;
                                    
                                    // If switching to full day, auto-select all destinations
                                    if (duration === "full") {
                                      setDestinations({
                                        delos: true,
                                        rhenia: true,
                                        mykonos: true
                                      });
                                    }
                                  };

  // Update the price when relevant parameters change
  useEffect(() => {
    if (
      selectedYachtDetails &&
      formData.bookingDate &&
      formData.cruiseType &&
      formData.numberOfGuests > 0
    ) {
      const price = calculatePrice();
      console.log(`Price calculated: ${price}`);

      if (price > 0) {
        setTotalPrice(price);
        setPriceBreakdown({
          cruiseType: formData.cruiseType,
          date: formData.bookingDate,
          basePrice: price,
          guestCount: formData.numberOfGuests,
        });
        // Clear availability error if price is successfully calculated
        setErrors((prev) => ({ ...prev, availability: undefined }));
      } else {
        setTotalPrice(0);
        setPriceBreakdown(null);
        // Error message is already set in calculatePriceManually if price is 0
      }
    }
  }, [
    calculatePrice,
    selectedYachtDetails,
    formData.bookingDate,
    formData.cruiseType,
    formData.numberOfGuests,
  ]);

  const validateDates = (newDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (newDate < today) {
      setErrors((prev) => ({
        ...prev,
        bookingDate: "Booking date cannot be in the past",
      }));
      return false;
    }

    setErrors((prev) => ({ ...prev, bookingDate: undefined }));
    return true;
  };

  const handleDateChange = (date) => {
    if (!validateDates(date)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      bookingDate: date,
    }));

    setDatePickerOpen(false);
    // No need to explicitly call calculatePrice here as the useEffect will handle it
  };

  const validateCoupon = async (code) => {
    if (!code) {
      setCouponStatus({ isValid: false, discount: 0, message: "" });
      return;
    }

    try {
      const response = await coupons.validate(code, totalPrice / 1.24); // Pass net amount
      setCouponStatus({
        isValid: true,
        discount: response.data.discount,
        message: "✓ Coupon applied successfully!",
      });
    } catch (error) {
      setCouponStatus({
        isValid: false,
        discount: 0,
        message: "✕ Invalid coupon code",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedYacht) newErrors.yacht = "Please select a yacht";
    if (!formData.bookingDate)
      newErrors.bookingDate = "Booking date is required";
    if (!formData.bookingTime)
      newErrors.bookingTime = "Booking time is required";
    if (!formData.cruiseType) newErrors.cruiseType = "Cruise type is required";
    if (!formData.numberOfGuests || formData.numberOfGuests < 1)
      newErrors.numberOfGuests = "Valid number of guests is required";
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    if (!formData.phone) newErrors.phone = "Phone number is required";

    if (
      selectedYachtDetails &&
      formData.numberOfGuests > selectedYachtDetails.guestCapacity
    ) {
      newErrors.numberOfGuests = `Maximum guest capacity is ${selectedYachtDetails.guestCapacity}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateInvoice = async (booking) => {
    const invoiceDiv = document.createElement("div");
    invoiceDiv.style.padding = "10px";
    invoiceDiv.style.position = "absolute";
    invoiceDiv.style.left = "-9999px";
    invoiceDiv.style.background = "white";
    invoiceDiv.style.width = "900px";

    const colors = {
      primary: "#D81919",
      secondary: "#990000",
      accent: "#FFEDED",
      dark: "#333333",
      light: "#FFFFFF",
      gray: "#F8F8F8",
    };

    const header = `
    <div style="display: flex; justify-content: space-between; margin-bottom: 40px; position: relative;">
      <div style="width: 66%; position: relative; overflow: hidden; height: 120px;">
        <div style="position: absolute; top: -20px; left: -200px; width: calc(100% + 220px); height: calc(100% + 40px); transform: skew(-15deg); z-index: 0; box-shadow: 0 3px 10px rgba(0,0,0,0.2);"></div>
        <img src="${logo}" alt="Logo" style="width: 85%; height: 100%; object-fit: contain; position: relative; z-index: 1; padding: 20px 20px 20px 40px;" />
      </div>
      <div style="width: 32%; text-align: right; padding: 20px; background: ${
        colors.gray
      }; border-right: 5px solid ${
      colors.primary
    }; box-shadow: 0 3px 8px rgba(0,0,0,0.1);">
        <h2 style="margin-bottom: 15px; color: ${
          colors.primary
        }; font-size: 22px; font-weight: bold;">Contact Information</h2>
        <p style="margin: 5px 0; color: ${
          colors.dark
        };">Epar. Odos Mykonos 846 00</p>
        <p style="margin: 5px 0; color: ${
          colors.dark
        };">info@seascapeexperiences.com</p>
        <p style="margin: 5px 0; color: ${
          colors.dark
        }; font-weight: bold;">+306941419012</p>
        <p style="margin: 5px 0; color: ${
          colors.primary
        };">www.seascapeexperiences.com</p>
      </div>
    </div>
    <div style="text-align: center; margin-bottom: 30px; padding: 10px 0; border-bottom: 2px solid ${
      colors.primary
    }; border-top: 2px solid ${colors.primary};">
      <h1 style="margin: 0; color: ${
        colors.primary
      }; font-size: 28px; text-transform: uppercase; letter-spacing: 2px;">PRE-INVOICE</h1>
      <p style="margin: 5px 0; color: ${
        colors.dark
      };">Pre-Invoice #: INV-${new Date().getFullYear()}-${Math.floor(
      Math.random() * 1000000
    )}</p>
      <p style="margin: 5px 0; color: ${
        colors.dark
      };">Pre-Invoice Date: ${new Date().toLocaleDateString("en-GB")}</p>
    </div>
  `;

    const infoSection = `
    <div style="display: flex; justify-content: space-between; margin-bottom: 40px; gap: 40px;">
      <div style="flex: 1; background: ${
        colors.accent
      }; padding: 25px; border-radius: 8px; border-left: 6px solid ${
      colors.primary
    }; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
        <h3 style="margin-bottom: 15px; color: ${
          colors.primary
        }; text-transform: uppercase; font-weight: bold; border-bottom: 2px solid ${
      colors.primary
    }; padding-bottom: 8px;">CLIENT DETAILS</h3>
        <p style="margin: 10px 0; font-weight: bold; font-size: 16px;">${
          booking.customer.firstName
        } ${booking.customer.lastName}</p>
        <p style="margin: 8px 0;"><span style="color: ${
          colors.secondary
        }; font-weight: bold;">Phone:</span> ${booking.customer.phoneNumber}</p>
        <p style="margin: 8px 0;"><span style="color: ${
          colors.secondary
        }; font-weight: bold;">Email:</span> ${booking.customer.email}</p>
        <p style="margin: 8px 0;"><span style="color: ${
          colors.secondary
        }; font-weight: bold;">Guests:</span> ${booking.numberOfGuests}</p>
      </div>
      <div style="flex: 1; background: ${
        colors.accent
      }; padding: 25px; border-radius: 8px; border-left: 6px solid ${
      colors.primary
    }; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
        <h3 style="margin-bottom: 15px; color: ${
          colors.primary
        }; text-transform: uppercase; font-weight: bold; border-bottom: 2px solid ${
      colors.primary
    }; padding-bottom: 8px;">YACHT INFORMATION</h3>
        <p style="margin: 10px 0; font-weight: bold; font-size: 16px;"><span style="color: ${
          colors.secondary
        }; font-weight: bold;">Yacht:</span> ${booking.yachtName}</p>
        <p style="margin: 8px 0;"><span style="color: ${
          colors.secondary
        }; font-weight: bold;">Cruise Type:</span> ${booking.cruiseType}</p>
        <p style="margin: 8px 0;"><span style="color: ${
          colors.secondary
        }; font-weight: bold;">Date:</span> ${new Date(
      booking.bookingDate
    ).toLocaleDateString("en-GB")}</p>
        <p style="margin: 8px 0;"><span style="color: ${
          colors.secondary
        }; font-weight: bold;">Time:</span> ${booking.bookingTime}</p>
      </div>
    </div>
  `;

    const destinations =
      booking.destination && booking.destination.length > 0
        ? `<p style="margin: 8px 0;"><span style="font-weight: bold;">Destinations:</span> ${booking.destination.join(
            ", "
          )}</p>`
        : "";

    const priceTable = `
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px; border: 2px solid ${
      colors.primary
    }; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
      <thead>
        <tr style="background: ${colors.primary}; color: white;">
          <th style="padding: 15px; text-align: left; font-size: 15px;">Description</th>
          <th style="padding: 15px; text-align: center; font-size: 15px;">Details</th>
          <th style="padding: 15px; text-align: right; font-size: 15px;">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr style="background-color: ${colors.light};">
          <td style="padding: 14px; border-bottom: 1px solid #ddd; font-weight: bold;">
            ${booking.cruiseType}
          </td>
          <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">
            ${new Date(booking.bookingDate).toLocaleDateString("en-GB")} at ${
      booking.bookingTime
    }<br>
            ${booking.numberOfGuests} guest(s)
            ${destinations}
          </td>
          <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: right; font-weight: bold;">
            €${booking.calculatedPrice.toFixed(2)}
          </td>
        </tr>
      </tbody>
    </table>
  `;

    const additionalNotesSection = booking.additionalNotes
      ? `
      <div style="margin-bottom: 20px; background: #f8f8f8; padding: 15px; border-left: 4px solid #1e3a8a;">
        <h4 style="margin-bottom: 10px; color: #1e3a8a; text-transform: uppercase; font-weight: bold;">Additional Notes</h4>
        <p style="margin: 0; font-style: italic;">${booking.additionalNotes}</p>
      </div>
    `
      : "";

    const totalsSection = `
    <div style="display: flex; justify-content: flex-end;">
      <div style="width: 350px;">
        <div style="border: 2px solid ${
          colors.primary
        }; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
          <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
            colors.accent
          };">
            <div style="display: flex; justify-content: space-between;">
              <span style="font-weight: bold;">Subtotal:</span>
              <span>€${(booking.calculatedPrice / 1.24).toFixed(2)}</span>
            </div>
          </div>
          ${
            booking.couponDiscountAmount > 0
              ? `
          <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
            colors.accent
          }; color: ${colors.primary};">
            <div style="display: flex; justify-content: space-between;">
              <span style="font-weight: bold;">Discount:</span>
              <span>-€${booking.couponDiscountAmount.toFixed(2)}</span>
            </div>
          </div>
          `
              : ""
          }
          <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
            colors.accent
          };">
            <div style="display: flex; justify-content: space-between;">
              <span style="font-weight: bold;">VAT (24%):</span>
              <span>€${(
                booking.calculatedPrice -
                booking.calculatedPrice / 1.24
              ).toFixed(2)}</span>
            </div>
          </div>
          <div style="padding: 16px; background: ${
            colors.primary
          }; color: white;">
            <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 18px;">
              <span>TOTAL:</span>
              <span>€${booking.calculatedPrice.toFixed(2)} (NOT PAID)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
    const footer = `
    <div style="margin-top: 50px;">
      <div style="margin-bottom: 30px; padding: 20px; background: ${colors.accent}; border-radius: 8px; border-left: 6px solid ${colors.primary}; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
        <p style="margin: 0; font-style: italic; color: ${colors.dark};">Thank you for choosing <span style="font-weight: bold; color: ${colors.primary};">SeaScape Experience Yacht Charters</span>. For assistance, contact us at <span style="font-weight: bold;">+306941419012</span> or <span style="font-weight: bold;">info@seascapeexperiences.com</span></p>
      </div>
      <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 30px;">
        <div style="width: 30%;">
          <div style="background: ${colors.primary}; height: 4px; margin-bottom: 5px;"></div>
          <div style="background: ${colors.primary}; height: 2px;"></div>
        </div>
        <div style="width: 200px; text-align: center;">
          <div style="border-bottom: 2px solid ${colors.primary}; margin-bottom: 8px; padding-bottom: 15px;">
            <img src="data:image/png;base64,..." alt="Signature" style="max-width: 150px;" />
          </div>
          <p style="margin: 0; font-weight: bold; color: ${colors.primary};">Management</p>
          <p style="margin: 0; color: ${colors.dark};">SeaScape Experiences</p>
        </div>
        <div style="width: 30%;">
          <div style="background: ${colors.primary}; height: 4px; margin-bottom: 5px;"></div>
          <div style="background: ${colors.primary}; height: 2px;"></div>
        </div>
      </div>
    </div>
    <div style="margin-top: 40px; text-align: center; padding-top: 20px; border-top: 2px dashed ${colors.accent};">
    </div>
  `;

    invoiceDiv.innerHTML =
      header +
      infoSection +
      priceTable +
      additionalNotesSection +
      totalsSection +
      footer;
    document.body.appendChild(invoiceDiv);

    try {
      const scale = 2;
      const canvas = await html2canvas(invoiceDiv, {
        scale: scale,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width / scale;
      const imgHeight = canvas.height / scale;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 20;

      pdf.addImage(
        imgData,
        "JPEG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio
      );

      // Add page numbers at the bottom
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(150);
      }

      // Instead of saving, return the PDF as base64
      const pdfData = pdf.output("datauristring");
      return pdfData.split(",")[1]; // Return just the base64 data
    } finally {
      document.body.removeChild(invoiceDiv);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Calculate final price including discount
      const netTotal = totalPrice / 1.24; // Remove VAT
      const discountAmount = couponStatus.isValid ? couponStatus.discount : 0;
      const netAfterDiscount = netTotal - discountAmount;
      const finalTotal = netAfterDiscount * 1.24; // Apply VAT

      // Calculate commission amount based on partner's rate
      const commissionAmount = (netAfterDiscount * partner.commissionRate) / 100;

      // Get selected destinations from the destinations state object
      const selectedDestinations = Object.entries(destinations)
        .filter(([_, isSelected]) => isSelected)
        .map(([name]) => name);

      const bookingData = {
        yachtId: selectedYacht._id, 
        yachtName: `${selectedYacht.builder} ${selectedYacht.name}`,
        bookingDate: new Date(formData.bookingDate).toISOString(),
        bookingTime: formData.bookingTime,
        destination: selectedDestinations,
        cruiseType: formData.cruiseType,
        numberOfGuests: formData.numberOfGuests,
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phone,
        },
        calculatedPrice: finalTotal,
        couponCode: formData.couponCode || undefined,
        couponDiscountAmount: discountAmount,
        professionalPartner: partner._id,
        commissionAmount: commissionAmount,
        additionalNotes: formData.notes,
        status: "pending",
      };

      // Generate PDF data
      const pdfData = await generateInvoice(bookingData);

      // Add PDF data to booking data
      bookingData.invoicePdf = pdfData;

      const response = await yachtBookings.create(bookingData);
      onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error("Error creating booking:", error);
      setErrors({ submit: "Failed to create booking. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const CustomInputComponent = ({
    value,
    onClick,
    placeholder,
    isError,
    icon: Icon,
  }) => (
    <div
      className={`w-full relative cursor-pointer ${
        isError ? "border-red-500" : "border-gray-300"
      }`}
    >
      <button
        type="button"
        className={`w-full flex items-center px-4 py-2 border ${
          isError ? "border-red-500 bg-red-50" : "border-gray-300"
        } rounded-md transition-all hover:border-blue-500`}
        onClick={onClick}
      >
        {Icon && <Icon className="text-gray-500 mr-2" />}
        {value || placeholder}
      </button>
    </div>
  );

  const CustomCheckbox = ({ checked, onChange, label }) => (
    <label className="flex items-center cursor-pointer group hover:bg-gray-50 p-2 rounded-md transition-colors">
      <div className="relative w-5 h-5 mr-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="opacity-0 absolute h-5 w-5 cursor-pointer"
        />
        <div
          className={`border-2 rounded-sm h-5 w-5 flex flex-shrink-0 justify-center items-center focus-within:border-blue-500 ${
            checked ? "bg-blue-500 border-blue-500" : "border-gray-300"
          }`}
        >
          {checked && (
            <svg
              className="fill-current w-3 h-3 text-white pointer-events-none"
              viewBox="0 0 20 20"
            >
              <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
            </svg>
          )}
        </div>
      </div>
      <span className="select-none text-gray-700">{label}</span>
    </label>
  );

  const filteredYachts = availableYachts.filter((yacht) => {
    const matchesSearch =
      !searchQuery ||
      `${yacht.builder} ${yacht.name}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesType = !selectedType || yacht.yachtType === selectedType;
    return matchesSearch && matchesType;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-6xl relative max-h-[90vh] overflow-hidden m-4"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-6 py-4 flex justify-between items-center sticky top-0 z-20">
          <div className="flex items-center space-x-3">
            <motion.div
              className="p-2 bg-blue-800/50 rounded-full"
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <FaShip className="text-white text-2xl" />
            </motion.div>
            <h2 className="text-xl font-bold text-white">
              Create Yacht Booking for {partner?.firstName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-800/50 rounded-full transition-colors text-white"
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Content */}
        {initialLoading ? (
          <div className="p-8 flex flex-col items-center justify-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-500">Loading yachts...</p>
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[calc(90vh-4rem)]">
            <div className="p-6">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Yacht Selection Column */}
                  <div className="lg:col-span-1">
                    <h3 className="text-lg font-semibold mb-4">
                      Select a Yacht
                    </h3>

                    {/* Search and Filter Controls */}
                    <div className="sticky top-0 bg-white z-10 space-y-3 mb-4 p-4 border border-gray-200 rounded-lg shadow-sm">
                      <input
                        type="text"
                        placeholder="Search yachts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />

                      <select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">All Yacht Types</option>
                        {Array.from(
                          new Set(
                            availableYachts.map((yacht) => yacht.yachtType)
                          )
                        ).map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="h-[calc(100vh-20rem)] overflow-y-auto border border-gray-200 rounded-lg">
                      <div className="divide-y divide-gray-200">
                        {filteredYachts.map((yacht) => (
                          <div
                            key={yacht._id}
                            className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                              selectedYacht?._id === yacht._id
                                ? "bg-blue-50"
                                : ""
                            }`}
                            onClick={() => handleYachtSelect(yacht)}
                          >
                            <div className="flex items-center space-x-4">
                              <div className="flex-shrink-0 h-14 w-14 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                                {yacht.images && yacht.images[0] ? (
                                  <img
                                    src={yacht.images[0]}
                                    alt={`${yacht.builder} ${yacht.name}`}
                                    className="h-full w-full object-contain"
                                  />
                                ) : (
                                  <FaShip className="text-gray-400 text-2xl" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {yacht.builder} {yacht.name}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {yacht.yachtType} •{" "}
                                  {yacht.specifications?.length}
                                </p>
                                <div className="flex items-center mt-1">
                                  <span className="text-sm text-gray-600">
                                    <FaUsers
                                      className="inline mr-1"
                                      size={12}
                                    />
                                    Max {yacht.guestCapacity} guests
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {filteredYachts.length === 0 && (
                          <div className="p-6 text-center text-gray-500">
                            No yachts found matching your criteria
                          </div>
                        )}
                      </div>
                    </div>
                    {errors.yacht && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.yacht}
                      </p>
                    )}
                  </div>

                  {/* Booking Details Column */}
                  <div className="lg:col-span-2 space-y-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Booking Details
                    </h3>

                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="font-medium text-gray-900 mb-4">
                            Cruise Details
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Cruise Type *
                              </label>
                              <select
                                value={formData.cruiseType}
                                onChange={handleCruiseTypeChange}
                                disabled={!selectedYacht}
                                className={`w-full px-4 py-2 border ${
                                  errors.cruiseType
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all ${
                                  !selectedYacht
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              >
                                <option value="">Select Cruise Type</option>
                                {cruiseOptions
                                  .filter(
                                    (option) =>
                                      !(
                                        option.value
                                          .toLowerCase()
                                          .includes("semi") &&
                                        option.value
                                          .toLowerCase()
                                          .includes("full")
                                      )
                                  )
                                  .map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label} ({option.durationHours}{" "}
                                      hours)
                                    </option>
                                  ))}
                              </select>
                              {errors.cruiseType && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.cruiseType}
                                </p>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Date *
                                </label>
                                <DatePicker
                                  selected={formData.bookingDate}
                                  onChange={handleDateChange}
                                  customInput={
                                    <CustomInputComponent
                                      placeholder="Select Date"
                                      isError={!!errors.bookingDate}
                                      icon={FaCalendarAlt}
                                    />
                                  }
                                  dateFormat="dd/MM/yyyy"
                                  minDate={new Date()}
                                  placeholderText="Select Date"
                                  open={datePickerOpen}
                                  onClickOutside={() =>
                                    setDatePickerOpen(false)
                                  }
                                  wrapperClassName="w-full"
                                  onInputClick={() => setDatePickerOpen(true)}
                                  disabled={!selectedYacht}
                                />
                                {errors.bookingDate && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {errors.bookingDate}
                                  </p>
                                )}
                              </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Booking Time *
                                  </label>
                                  <div data-error={!!errors.bookingTime}>
                                  <select
                                    value={formData.bookingTime}
                                    onChange={(e) => setFormData({...formData, bookingTime: e.target.value})}
                                    className={`w-full h-[42px] px-3 border rounded bg-white appearance-none hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    errors.bookingTime ? 'border-red-500' : 'border-gray-200'
                                    }`}
                                    disabled={!formData.cruiseType}
                                  >
                                    <option value="">Select a time</option>
                                    
                                    {/* Morning option for half-day cruises */}
                                    {formData.cruiseType && formData.cruiseType.toLowerCase().includes('half') && (
                                    <option value="Morning: 10:00 - 15:00">Morning: 10:00 - 15:00</option>
                                    )}
                                    
                                    {/* Sunset option for half-day cruises */}
                                    {formData.cruiseType && formData.cruiseType.toLowerCase().includes('half') && (
                                    <option value="Sunset: 15:30 - 20:30">Sunset: 15:30 - 20:30</option>
                                    )}
                                    
                                    {/* Full day cruise options */}
                                    {formData.cruiseType && formData.cruiseType.toLowerCase().includes('full') && (
                                    <>
                                      <option value="10:00 - 18:00">10:00 - 18:00</option>
                                      <option value="11:00 - 19:00">11:00 - 19:00</option>
                                      <option value="12:00 - 20:00">12:00 - 20:00</option>
                                      <option value="13:00 - 21:00">13:00 - 21:00</option>
                                    </>
                                    )}
                                  </select>
                                  </div>
                                  {errors.bookingTime && (
                                  <p className="text-red-500 text-xs mt-1">{errors.bookingTime}</p>
                                  )}
                                  {!formData.cruiseType && (
                                  <p className="text-gray-500 text-xs mt-1">Please select a cruise type first</p>
                                  )}
                                  
                                  {/* Time slots may change notice for certain months */}
                                  {formData.bookingDate && [8, 9].includes(formData.bookingDate.getMonth()) && (
                                  <p className="text-amber-600 text-xs mt-1 italic">
                                    <FaInfoCircle className="inline mr-1" />
                                    Time slots may vary during September & October due to sunset times
                                  </p>
                                  )}
                                </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Number of Guests *
                              </label>
                              <input
                                type="number"
                                min="1"
                                max={selectedYachtDetails?.guestCapacity}
                                value={formData.numberOfGuests}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value, 10);
                                  setFormData((prev) => ({
                                    ...prev,
                                    numberOfGuests: value,
                                  }));

                                  // Recalculate price if all required data is available
                                  if (
                                    formData.bookingDate &&
                                    formData.cruiseType &&
                                    value > 0
                                  ) {
                                    calculatePrice(
                                      formData.bookingDate,
                                      formData.cruiseType,
                                      value
                                    );
                                  }
                                }}
                                disabled={!selectedYacht}
                                className={`w-full px-4 py-2 border ${
                                  errors.numberOfGuests
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all ${
                                  !selectedYacht
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                              {errors.numberOfGuests && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.numberOfGuests}
                                </p>
                              )}
                              {selectedYachtDetails && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Maximum capacity:{" "}
                                  {selectedYachtDetails.guestCapacity} guests
                                </p>
                              )}
                            </div>


                            {formData.cruiseType && (
                              <div className="space-y-3">
                                <label className="block text-sm font-medium text-gray-700">
                                  Destinations *
                                  {formData.cruiseType.toLowerCase().includes('half') ? (
                                    <span className="text-xs text-gray-500 ml-2">(Please select one)</span>
                                  ) : (
                                    <span className="text-xs text-gray-500 ml-2">(All included)</span>
                                  )}
                                </label>
                                
                                <div className="bg-white rounded-lg border border-gray-200 p-3">
                                  {/* Set cruise duration based on cruise type */}
                                  <input 
                                    type="hidden" 
                                    value={formData.cruiseType.toLowerCase().includes('half') ? "half" : "full"}
                                    onChange={(e) => handleDurationChange(e)}
                                  />
                                                                   
                                  <div className="space-y-2">
                                    <label className="flex items-start p-2 hover:bg-gray-50 rounded cursor-pointer">
                                      <input
                                        type="checkbox"
                                        name="delos"
                                        className="mt-1 mr-2"
                                        checked={destinations.delos}
                                        onChange={handleDestinationChange}
                                        disabled={formData.cruiseType.toLowerCase().includes('full')}
                                      />
                                      <div>
                                        <span className="font-medium text-gray-800 block">Delos Island</span>
                                        <span className="text-sm text-gray-600">Discover ancient ruins and mythological wonders on this UNESCO World Heritage site.</span>
                                      </div>
                                    </label>
                                    
                                    <label className="flex items-start p-2 hover:bg-gray-50 rounded cursor-pointer">
                                      <input
                                        type="checkbox"
                                        name="rhenia"
                                        className="mt-1 mr-2"
                                        checked={destinations.rhenia}
                                        onChange={handleDestinationChange}
                                        disabled={formData.cruiseType.toLowerCase().includes('full')}
                                      />
                                      <div>
                                        <span className="font-medium text-gray-800 block">Rhenia Island</span>
                                        <span className="text-sm text-gray-600">Escape to pristine beaches and crystal-clear waters, perfect for a relaxing getaway.</span>
                                      </div>
                                    </label>
                                    
                                    <label className="flex items-start p-2 hover:bg-gray-50 rounded cursor-pointer">
                                      <input
                                        type="checkbox"
                                        name="mykonos"
                                        className="mt-1 mr-2"
                                        checked={destinations.mykonos}
                                        onChange={handleDestinationChange}
                                        disabled={formData.cruiseType.toLowerCase().includes('full')}
                                      />
                                      <div>
                                        <span className="font-medium text-gray-800 block">Mykonos Island</span>
                                        <span className="text-sm text-gray-600">Uncover the beauty of Mykonos with its picturesque South Coastline and famous beaches.</span>
                                      </div>
                                    </label>
                                  </div>
                                  
                                  {/* Help text based on cruise type */}
                                  <p className="text-xs text-gray-500 mt-2 italic">
                                    {formData.cruiseType.toLowerCase().includes('half') ? (
                                      <span>Half-day cruises allow visiting one destination of your choice</span>
                                    ) : (
                                      <span>Full-day cruises include all destinations</span>
                                    )}
                                  </p>
                                </div>
                                
                                {/* Make sure at least one destination is selected */}
                                {formData.cruiseType.toLowerCase().includes('half') && 
                                  !destinations.delos && 
                                  !destinations.rhenia && 
                                  !destinations.mykonos && (
                                    <p className="text-amber-600 text-xs">
                                      <FaInfoCircle className="inline mr-1" />
                                      Please select one destination for your half-day cruise
                                    </p>
                                  )}
                              </div>
                            )}
                          </div>

                          {errors.availability && (
                            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                              <p className="text-yellow-800 text-sm">
                                {errors.availability}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Customer Information */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="font-medium text-gray-900 mb-4">
                            Customer Information
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                First Name *
                              </label>
                              <input
                                type="text"
                                placeholder="First Name"
                                value={formData.firstName}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    firstName: e.target.value,
                                  })
                                }
                                disabled={!selectedYacht}
                                className={`w-full px-4 py-2 border ${
                                  errors.firstName
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all hover:border-blue-500 ${
                                  !selectedYacht
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                              {errors.firstName && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.firstName}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Last Name *
                              </label>
                              <input
                                type="text"
                                placeholder="Last Name"
                                value={formData.lastName}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    lastName: e.target.value,
                                  })
                                }
                                disabled={!selectedYacht}
                                className={`w-full px-4 py-2 border ${
                                  errors.lastName
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all hover:border-blue-500 ${
                                  !selectedYacht
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                              {errors.lastName && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.lastName}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email Address *
                              </label>
                              <input
                                type="email"
                                placeholder="Email Address"
                                value={formData.email}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    email: e.target.value,
                                  })
                                }
                                disabled={!selectedYacht}
                                className={`w-full px-4 py-2 border ${
                                  errors.email
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all hover:border-blue-500 ${
                                  !selectedYacht
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                              {errors.email && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.email}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number *
                              </label>
                              <input
                                type="tel"
                                placeholder="Phone Number"
                                value={formData.phone}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    phone: e.target.value,
                                  })
                                }
                                disabled={!selectedYacht}
                                className={`w-full px-4 py-2 border ${
                                  errors.phone
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all hover:border-blue-500 ${
                                  !selectedYacht
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                              {errors.phone && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.phone}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Additional Options & Coupon */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="font-medium text-gray-900 mb-4">
                            Discounts & Notes
                          </h4>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Additional Notes:
                              </label>
                              <textarea
                                value={formData.notes}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    notes: e.target.value,
                                  })
                                }
                                rows="3"
                                disabled={!selectedYacht}
                                className={`w-full p-3 border border-gray-200 rounded bg-white hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  !selectedYacht
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                                placeholder="Any special requests or notes"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Coupon Code
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder="Enter coupon code"
                                  value={formData.couponCode}
                                  onChange={(e) => {
                                    const code = e.target.value.toUpperCase();
                                    setFormData({
                                      ...formData,
                                      couponCode: code,
                                    });
                                  }}
                                  onBlur={() =>
                                    validateCoupon(formData.couponCode)
                                  }
                                  disabled={!selectedYacht || !totalPrice}
                                  className={`w-full px-4 py-2 border rounded-md transition-all ${
                                    couponStatus.isValid
                                      ? "border-green-500 focus:ring-green-500"
                                      : couponStatus.message
                                      ? "border-red-500 focus:ring-red-500"
                                      : "border-gray-200 focus:ring-blue-500 hover:border-blue-500"
                                  } ${
                                    !selectedYacht || !totalPrice
                                      ? "bg-gray-100 cursor-not-allowed"
                                      : ""
                                  }`}
                                />
                                {couponStatus.message && (
                                  <div
                                    className={`text-sm mt-1 ${
                                      couponStatus.isValid
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {couponStatus.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Price Breakdown */}
                        {priceBreakdown && (
                          <div className="bg-blue-900 p-4 rounded-lg shadow-lg text-white">
                            <h4 className="font-medium text-white mb-4 flex items-center">
                              <FaMoneyBillWave className="mr-2" />
                              Price Breakdown
                            </h4>

                            <div className="space-y-2">
                              <div className="flex justify-between items-center text-sm">
                                <span>
                                  {priceBreakdown.cruiseType} on{" "}
                                  {priceBreakdown.date.toLocaleDateString(
                                    "en-GB"
                                  )}
                                </span>
                                <span>
                                  {priceBreakdown.guestCount} guest
                                  {priceBreakdown.guestCount !== 1 ? "s" : ""}
                                </span>
                              </div>

                              <div className="flex justify-between items-center text-sm text-gray-200 pt-2 border-t border-blue-800">
                                <span>Subtotal:</span>
                                <span>€{(totalPrice / 1.24).toFixed(2)}</span>
                              </div>

                              {couponStatus.isValid && (
                                <div className="flex justify-between items-center text-sm text-green-300">
                                  <span>Coupon Discount:</span>
                                  <span>
                                    -€{couponStatus.discount.toFixed(2)}
                                  </span>
                                </div>
                              )}

                              <div className="flex justify-between items-center text-sm text-gray-200">
                                <span>VAT (24%):</span>
                                <span>
                                  €{((totalPrice / 1.24) * 0.24).toFixed(2)}
                                </span>
                              </div>

                              <div className="flex justify-between items-center text-sm text-green-300">
                                <span>
                                  Commission ({partner.commissionRate}%):
                                </span>
                                <span>
                                  €
                                  {(
                                    ((totalPrice / 1.24) *
                                      partner.commissionRate) /
                                    100
                                  ).toFixed(2)}
                                </span>
                              </div>

                              <div className="flex justify-between items-center font-bold text-lg mt-2 pt-2 border-t border-blue-800">
                                <span>Total:</span>
                                <span>€{totalPrice.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {errors.submit && (
                          <p className="text-red-500 text-center">
                            {errors.submit}
                          </p>
                        )}

                        {/* Submit Button */}
                        <div className="flex justify-end">
                          <button
                            type="submit"
                            disabled={loading || !selectedYacht || !totalPrice}
                            className="px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                          >
                            {loading ? (
                              <>
                                <LoadingSpinner
                                  size="sm"
                                  className="text-white"
                                />
                                <span>Creating Booking...</span>
                              </>
                            ) : (
                              <span>Create Booking</span>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        <style jsx="true">{`
          .react-datepicker__day--selected {
            background-color: #1e3a8a !important;
            color: white !important;
          }

          .react-datepicker__time-list-item--selected {
            background-color: #1e3a8a !important;
            color: white !important;
          }
        `}</style>
      </motion.div>
    </div>
  );
};

export default CreatePartnerYachtBookingModal;
